import React, { useEffect, useState } from "react";
import Breadcrumb from "../../components/common/BreadCrumb";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import RippleLoader from "../../components/RippleLoader/RippleLoader";
import {
  AddPrimaryButton,
  DatePicker,
  DefaultButton,
  DefaultPrimaryButton,
  InputWithLabel,
  RadioButton,
  SelectWithLabel,
} from "../../components/micro/input";
import { useLocation, useNavigate } from "react-router-dom";
import ConfirmationModel from "../../components/common/ConfirmationModel";
import {
  CameraIcon,
  CancelIcon,
  Close,
  DeleteIcon,
  Plus,
} from "../../assets/svg";
import { useDispatch, useSelector } from "react-redux";
import { companiesList, getOutlet } from "../../redux/companies/slice";
import {
  createService,
  getServiceById,
  getTechnicianList,
} from "../../redux/service/slice";
import { getProductList, uploadImage } from "../../redux/product/slice";
import { getStaffsList } from "../../redux/staffs/slice";
import moment from "moment";
import { Cancel } from "@mui/icons-material";
import { Chip, IconButton } from "@mui/material";
import { ClearIcon } from "@mui/x-date-pickers";
import FileUploadChoose from "../../components/common/FileUploadChoose";
import { toast } from "react-toastify";
import { InputError } from "../../components/error/error";

const CreateEditRequest = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const isEditMode = location?.state?.mode;
  const companyData = location?.state?.companyData;
  const [isLoading, setIsLoading] = useState(false);
  const [formValues, setFormValues] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const serviceData = location?.state?.service;
  const [reinitialize, setReinitialize] = useState(!!serviceData?.id);
  const [requestTypes, setRequestTypes] = useState([
    {
      repair: "",
      followRepair: "",
      servicing: "",
      request_type_id: "",
      services: [
        {
          id: "",
          title: "",
          items: "",
          quantity: "",
          pricing: "",
          type: "",
          description: "",
          is_mobile: false,
        },
      ],
    },
  ]);
  const [imageUrls, setImageUrls] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [loadings, setLoadings] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [contractData, setContractData] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [outletOptions, setOutletOptions] = useState([]);
  const [selectedOutlet, setSelectedOutlet] = useState(
    serviceData?.id
      ? serviceData.outlets?.map((outlet) => ({
          value: outlet?.outlet?.id || "",
          label: outlet?.outlet?.name || "",
        }))
      : null
  );
  const [companyKey, setCompanyKey] = useState(0);

  // Selector
  const {
    outletList,
    techniciansList,
    companiesListData,
    productsList,
    staffsList,
    getServiceListData,
    loading,
  } = useSelector((store) => ({
    outletList: store?.companiesData?.getOutletStatus?.data?.data,
    techniciansList: store?.technicianData?.getTechnicianListStatus?.data,
    companiesListData: store?.companiesData?.companiesListStatus?.data?.data,
    productsList: store?.productsData?.getProductListStatus?.data,
    staffsList: store?.staffsData?.getStaffsListStatus?.data,
    // get service
    getServiceListData: store?.serviceData?.getServiceByIdStatus?.data?.data,
    loading: store?.serviceData?.loading,
  }));

  const outletIds = selectedOutlet?.map((outlet) => outlet.value) || [];

  useEffect(() => {
    if (serviceData?.id) {
      dispatch(getServiceById(serviceData?.id));
    }
    dispatch(
      getOutlet({
        search: "",
        pageLimit: "all",
        company_id: companyData?.id || "",
        sort_by: "name",
        sort_direction: "asc",
      })
    );
    dispatch(
      getTechnicianList({
        pageLimit: "all",
      })
    );
    dispatch(
      companiesList({
        search: "",
        pageLimit: "all",
        status: "active",
        sort_by: "name",
        sort_direction: "desc",
        contract: "all",
      })
    );
    dispatch(
      getStaffsList({
        search: "",
        pageLimit: "all",
        sort_by: "name",
        sort_direction: "desc",
      })
    );
  }, [serviceData?.id]);

  useEffect(() => {
    const outletIds = selectedOutlet?.map((outlet) => outlet.value) || [];

    dispatch(
      getProductList({
        search: "",
        pageCount: "",
        pageLimit: "all",
        sort_by: "name",
        sort_direction: "desc",
        outlet_id: outletIds,
      })
    );
  }, [selectedOutlet]);

  // get data on edit time
  useEffect(() => {
    if (serviceData?.id && getServiceListData?.data?.length > 0) {
      setContractData(getServiceListData?.data[0]);
    } else {
      setContractData(null);
    }
  }, [serviceData?.id, getServiceListData]);

  const techniciansOptions = Array.isArray(
    techniciansList?.data || techniciansList
  )
    ? (techniciansList?.data || techniciansList).map((technician) => ({
        value: technician?.id,
        label: technician?.full_name,
      }))
    : [];

  // Company options
  const companyOptions = Array.isArray(
    companiesListData?.data || companiesListData
  )
    ? (companiesListData?.data || companiesListData).map((company) => ({
        value: company?.id,
        label: company?.name,
      }))
    : [];

  // Filter customer options based on selected company
  useEffect(() => {
    if (selectedCompany) {
      const filteredCustomers = Array.isArray(staffsList?.data)
        ? staffsList.data
            .filter((customer) => customer.company_id === selectedCompany)
            .map((customer) => ({
              value: customer.id,
              label: customer.full_name,
            }))
        : [];
      setCustomerOptions(filteredCustomers);
    }
  }, [companyKey, staffsList]);

  // Filter outlet options based on selected company
  useEffect(() => {
    if (selectedCompany) {
      const filteredOutlets = Array.isArray(outletList)
        ? outletList
            .filter((outlet) => outlet.company_id === selectedCompany)
            .map((outlet) => ({
              value: outlet.id,
              label: outlet.name,
            }))
        : [];
      setOutletOptions(filteredOutlets);
    }
  }, [companyKey, outletList]);

  // On Edit, set the selected company and customer
  useEffect(() => {
    if (serviceData?.id && contractData?.company) {
      setSelectedCompany(contractData?.company?.id);
    }
  }, [serviceData?.id, contractData?.company?.id]);

  const handleCompanyChange = (companyId) => {
    setSelectedCompany(companyId);
    setCompanyKey(Date.now());
  };

  const itemOptions = Array.isArray(productsList?.data || productsList)
    ? (productsList?.data || productsList)
        .filter((item) => item?.is_active === 1)
        .map((item) => ({
          value: item?.id,
          label: item?.name || "Unnamed Item",
          ...item,
        }))
    : [];

  const findItemLabel = (itemId) => {
    const foundItem = itemOptions?.find(
      (item) => item?.value === itemId?.item_id
    );
    return foundItem ? foundItem?.label : "";
  };

  const handleAddClick = () => {
    setShowConfirmationModal(true);
  };

  // Create Edit API Call
  const handleConfirm = async (values, resetForm) => {
    setIsLoading(true);
    const payload = {
      servicing_request_id: 1,
      service_quotation_id: "",
      sales_id: "",
      company_id: values?.companyName?.value || values.companyName,
      customer_id: values?.customerFullName?.value || values?.customerFullName,
      outlet_ids: Array.isArray(values?.outlet)
        ? values.outlet.map((outlet) => outlet?.value)
        : [],
      technician_ids: Array.isArray(values?.technician)
        ? values.technician.map((tech) => tech?.value)
        : [],
      address: values?.address,
      postal_code: values?.postalCode,
      contactNumber: values?.contactNumber,
      email: values?.email,
      date: values.date ? moment(values.date).format("DD-MM-YYYY") : null,
      remark: values?.remark,
      time_range: values?.timeRange?.value,
      request_types: values?.requestTypes?.map((requestType) => {
        const firstService = requestType?.services?.[0];

        const item = firstService
          ? {
              title: firstService?.title,
              description: firstService?.description,
              type: Array.isArray(firstService?.type)
                ? firstService.type.map((t) =>
                    typeof t === "string" ? t : t?.value
                  )
                : firstService?.type
                ? [
                    typeof firstService.type === "string"
                      ? firstService.type
                      : firstService.type?.value,
                  ]
                : [],
              item_details: requestType?.services?.map((service) => ({
                id: service?.id || "",
                item_id: service?.item?.value || "",
                quantity: service?.quantity || "",
                price: service?.pricing || "",
              })),
              ...(firstService?.id && { id: firstService.id }),
            }
          : null;

        return {
          id: requestType?.id,
          request_type_id: requestType?.request_type_id,
          items: item ? [item] : [],
          service_images: imageUrls,
        };
      }),
    };

    const action = serviceData?.id
      ? createService({
          id: serviceData.id,
          ...payload,
          ...(isEditMode === "duplicate" ? { duplicate: serviceData.id } : {}),
        })
      : createService(payload);

    setIsLoading(true);
    try {
      const res = await dispatch(action);
      if (res.type === "createService/fulfilled") {
        navigate("/services");
      }
    } catch (error) {
      console.error("Error while creating service:", error);
    } finally {
      setIsLoading(false);
      setShowConfirmationModal(false);
    }
  };

  // Add Request
  const handleAddRequestType = () => {
    const newRequestType = {
      repair: "",
      followRepair: "",
      servicing: "",
      services: [
        {
          id: "",
          title: "",
          item: "",
          quantity: "",
          pricing: "",
          type: [],
          description: "",
          is_mobile: isEditMode === "edit" && contractData?.is_mobile === 1,
        },
      ],
    };
    setRequestTypes((prevRequestTypes) => [
      ...prevRequestTypes,
      newRequestType,
    ]);
  };

  // Add Service
  const handleAddService = (requestIndex) => {
    const newService = {
      id: "",
      item: "",
      quantity: "",
      pricing: "",
    };

    let prevRequestTypes = [...requestTypes];
    prevRequestTypes[requestIndex].services.push(newService);

    setRequestTypes(prevRequestTypes);
  };

  const handleRolesChange = (
    e,
    requestIndex,
    setFieldValue,
    values,
    serviceIndex
  ) => {
    const roleValue = e.target.value;
    const isChecked = e.target.checked;

    const currentTypes =
      values?.requestTypes?.[requestIndex]?.services?.[serviceIndex]?.type ||
      [];

    const updatedTypes = isChecked
      ? [...currentTypes, roleValue]
      : currentTypes.filter((type) => type !== roleValue);

    setFieldValue(
      `requestTypes[${requestIndex}].services[${serviceIndex}].type`,
      updatedTypes
    );
  };

  // call this on onchange
  const UpdateValue = (requestIndex, serviceIndex, key, value) => {
    setRequestTypes((prevState) => {
      const updatedRequests = [...prevState];

      if (serviceIndex === null) {
        updatedRequests[requestIndex][key] = value;
      } else {
        updatedRequests[requestIndex].services[serviceIndex][key] = value;
      }

      return updatedRequests;
    });
  };

  // Remove service
  const handleRemoveService = (requestIndex, serviceIndex) => {
    let prevRequestTypes = [...requestTypes];

    prevRequestTypes[requestIndex].services.splice(serviceIndex, 1);

    setRequestTypes(prevRequestTypes);
  };
  // Remove request
  const handleRemoveRequest = (requestIndex, serviceIndex) => {
    setRequestTypes((prevRequestTypes) => {
      const updatedRequestTypes = [...prevRequestTypes];

      updatedRequestTypes[requestIndex].services.splice(serviceIndex, 1);

      if (updatedRequestTypes[requestIndex].services.length === 0) {
        updatedRequestTypes.splice(requestIndex, 1);
      }

      return updatedRequestTypes;
    });
  };

  // Time Range
  const handleTimeRangeChange = () => {
    return [
      { value: "12:00AM_to_09:00AM", label: "12:00 AM to 09:00 AM" },
      { value: "09:00AM_to_12:00PM", label: "09:00 AM to  12:00 PM" },
      { value: "02:00PM_to_6:00PM", label: "02:00 PM to  06:00 PM" },
      { value: "06:00PM_to_10:00PM", label: "06:00 PM to  10:00 PM" },
      { value: "10:00PM_to_12:00AM", label: "10:00 PM to  12:00 AM" },
    ];
  };

  // Types
  const mockRoles = [
    { id: 1, value: "electrical", label: "Electrical" },
    { id: 2, value: "gas", label: "Gas" },
    { id: 3, value: "exhaust", label: "Exhaust" },
    { id: 4, value: "plumbing", label: "Plumbing" },
  ];

  // get request types on edit
  useEffect(() => {
    if (serviceData?.id) {
      const serviceRequestItems = contractData?.service_requests?.map(
        (serviceRequest) => ({
          request_type_id: serviceRequest?.request_type_id || "",
          id: serviceRequest?.id || "",
          services:
            serviceRequest?.service_request_items?.flatMap((item) => {
              const matchedTypes =
                item?.service_request_item_types
                  ?.filter((typeItem) => {
                    return (
                      (!typeItem.item_id || typeItem.item_id === item.id) &&
                      typeItem.service_request_id === serviceRequest?.id
                    );
                  })
                  ?.map((typeItem) => typeItem.type) || [];

              return item?.service_request_item_details?.map((detail) => {
                return {
                  title: item?.title || "",
                  item: {
                    id: detail?.item_id || "",
                    value: detail?.item_id || "",
                    label: findItemLabel(detail),
                  },
                  quantity: detail?.quantity || "",
                  pricing: detail?.price || "",
                  id: item?.id,
                  type: matchedTypes,
                  description: item?.description || "",
                  is_mobile: contractData?.is_mobile === 1,
                };
              });
            }) || [],
        })
      );
      setRequestTypes(serviceRequestItems);
    }
  }, [serviceData?.id, getServiceListData, productsList]);

  const timeRange = serviceData?.id
    ? handleTimeRangeChange().find(
        (option) =>
          option.label.replace(/\s+/g, " ").trim() ===
          contractData?.timeRange.replace(/\s+/g, " ").trim()
      ) || null
    : null;

  const handleDeleteOutlet = (e, value, values, setFieldValue) => {
    e.stopPropagation();

    const updatedOutlets = values?.outlet?.filter(
      (outlet) => outlet.value !== value
    );

    setFieldValue("outlet", updatedOutlets);
  };

  const handleDeleteTechnician = (e, value, values, setFieldValue) => {
    e.stopPropagation();

    const updatedTechnicians = values?.technician?.filter(
      (technician) => technician.value !== value
    );

    setFieldValue("technician", updatedTechnicians);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  // upload image or video
  const handleFileChange = async (event, setFieldValue) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    const formData = new FormData();
    files.forEach((file) => formData.append("file[]", file));
    formData.append("type", "service_image");

    try {
      setLoadings(true);
      const response = await dispatch(uploadImage(formData));
      const uploadedData = response.payload?.data;

      if (
        response.type === "uploadImage/fulfilled" &&
        uploadedData?.length > 0
      ) {
        const uploadedFilesData = uploadedData.map((file, index) => ({
          url: file.url || URL.createObjectURL(files[index]),
          type: files[index].type,
          name: file.name,
          id: file.id,
        }));

        setUploadedFiles((prev) => [...prev, ...uploadedFilesData]);
        setFieldValue("uploadedFiles", (prev = []) => [
          ...prev,
          ...uploadedFilesData,
        ]);

        const uploadedImageIds = uploadedFilesData.map((img) => img.id);
        setImageUrls((prev) => [...prev, ...uploadedImageIds]);

        toast.success("Images uploaded successfully!");
      } else {
        throw new Error("Image upload failed");
      }
    } catch (error) {
      toast.error(
        "File must be a valid format (jpg, png, mp4, etc). Please try again."
      );
    } finally {
      setLoadings(false);
    }
  };

  // get image or video
  useEffect(() => {
    if (
      isEditMode === "edit" &&
      contractData?.is_mobile === 1 &&
      Array.isArray(contractData?.images)
    ) {
      const inferMimeType = (url) => {
        const ext = url?.split(".").pop().toLowerCase();
        if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext))
          return `image/${ext === "jpg" ? "jpeg" : ext}`;
        if (["mp4", "webm", "ogg"].includes(ext)) return `video/${ext}`;
        return "application/octet-stream"; // fallback
      };

      const formattedImages = contractData.images.map((img) => ({
        url: img?.document?.url,
        type: inferMimeType(img?.document?.url),
        id: img?.document_id,
      }));

      setUploadedFiles(formattedImages);
      setImageUrls(formattedImages.map((img) => img.id));
    }
  }, [isEditMode, contractData]);

  // Delete image & video
  const handleDelete = (index, setFieldValue) => {
    const updatedFiles = uploadedFiles.filter((_, i) => i !== index);
    setUploadedFiles(updatedFiles);
    setFieldValue("uploadedFiles", updatedFiles);

    const updatedImageUrls = imageUrls.filter((_, i) => i !== index);
    setImageUrls(updatedImageUrls);
  };

  return (
    <>
      <div>
        <div className="flex justify-between items-start">
          <div>
            <h6 className="font-Montserrat font-medium text-xl mb-3">
              {isEditMode === "duplicate"
                ? "Duplicate Service Request"
                : isEditMode === "edit"
                ? "Edit Service Request"
                : "Create Service Request"}
            </h6>
            <Breadcrumb
              breadCrumbParent={"Home"}
              breadCrumbActive={
                isEditMode === "duplicate"
                  ? "Duplicate Service Request"
                  : isEditMode === "edit"
                  ? "Edit Service Request"
                  : "Create Service Request"
              }
              breadCrumbParent2={"Service Request"}
              breadCrumbTitleKey={"/"}
            />
          </div>
        </div>
        {isLoading || loading ? (
          <RippleLoader />
        ) : (
          <Formik
            enableReinitialize={reinitialize}
            initialValues={{
              companyName: serviceData?.id
                ? {
                    value: contractData?.company?.id || "",
                    label: contractData?.company?.name || "",
                  }
                : null,
              customerFullName: serviceData?.id
                ? {
                    value: contractData?.customers?.id || "",
                    label: contractData?.customers?.full_name || "",
                  }
                : null,
              outlet: serviceData?.id
                ? serviceData.outlets?.map((outlet) => ({
                    value: outlet?.outlet?.id || "",
                    label: outlet?.outlet?.name || "",
                  }))
                : [],
              technician: serviceData?.id
                ? serviceData.technicians?.map((technician) => ({
                    value: technician?.id || "",
                    label: technician?.full_name || "",
                  }))
                : [],
              address: serviceData?.id ? contractData?.address || "" : "",
              postalCode: serviceData?.id
                ? contractData?.postal_code || ""
                : "",
              contactNumber: serviceData?.id ? contractData?.phone || "" : "",
              timeRange: serviceData?.id ? timeRange : null,
              email: serviceData?.id ? contractData?.email || "" : "",
              date: serviceData?.id
                ? contractData?.date
                  ? moment(contractData?.date, "DD/MM/YYYY")
                  : moment()
                : "",
              remark: serviceData?.id ? contractData?.remarks || "" : "",
              requestTypes: requestTypes,
              uploadedFiles:
                serviceData?.id && Array.isArray(contractData?.images)
                  ? contractData.images.map((img) => ({
                      url: img?.document?.url,
                      type: "image",
                      id: img?.id,
                    }))
                  : [],
              is_mobile:
                isEditMode === "edit" && contractData?.is_mobile === 1
                  ? true
                  : false,
            }}
            validationSchema={Yup.object().shape({
              companyName: Yup.object().required("Please select company name"),
              customerFullName: Yup.object()
                .nullable()
                .test(
                  "company-required",
                  "Please select company first",
                  function (value) {
                    if (!this.parent.companyName) {
                      return this.createError({
                        path: "customerFullName",
                        message: "Please select company first",
                      });
                    }
                    return true;
                  }
                )
                .test(
                  "customer-required",
                  "Please select customer name",
                  function (value) {
                    if (this.parent.companyName && !value) {
                      return this.createError({
                        path: "customerFullName",
                        message: "Please select customer name",
                      });
                    }
                    return true;
                  }
                ),
              address: Yup.string()
                .trim()
                .min(3)
                .required("Please enter address"),
              date: Yup.date().required("Please select date"),
              timeRange: Yup.object().required("Please select a time range"),
              postalCode: Yup.string()
                .required("Please enter postal code")
                .length(6, "Postal code must be exactly 6 digit")
                .matches(/^\d+$/, "Postal code must be numeric"),
              requestTypes: Yup.array().of(
                Yup.object().shape({
                  request_type_id: Yup.string().required(
                    "Request type is required"
                  ),
                  services: Yup.array().of(
                    Yup.object().shape({
                      title: Yup.string()
                        .trim()
                        .when("$serviceIndex", {
                          is: 0,
                          then: (schema) =>
                            Yup.string()
                              .trim()
                              .when("is_mobile", {
                                is: false,
                                then: (s) => s.required("Title is required"),
                                otherwise: (s) => s.notRequired(),
                              }),
                          otherwise: () => Yup.string().notRequired(),
                        }),

                      item: Yup.object()
                        .nullable()
                        .required("Item is required"),
                      quantity: Yup.number()
                        .typeError("Quantity must be a number")
                        .required("Quantity is required"),
                      pricing: Yup.number()
                        .typeError("Pricing must be a number")
                        .required("Pricing is required"),
                      type: Yup.array()
                        .of(Yup.string())
                        .when("$serviceIndex", {
                          is: 0,
                          then: (schema) => schema.min(1, "Type is required"),
                          otherwise: (schema) => schema.notRequired(),
                        }),
                      description: Yup.string()
                        .trim()
                        .when("$serviceIndex", {
                          is: 0,
                          then: (schema) =>
                            Yup.string()
                              .trim()
                              .when("is_mobile", {
                                is: false,
                                then: (s) =>
                                  s.required("Description is required"),
                                otherwise: (s) => s.notRequired(),
                              }),
                          otherwise: () => Yup.string().notRequired(),
                        }),
                    })
                  ),
                })
              ),
            })}
            context={{ serviceIndex: 0 }}
            onSubmit={(values, { setSubmitting }) => {
              setFormValues(values);
              handleAddClick();
              setSubmitting(false);
            }}
          >
            {({
              values,
              errors,
              touched,
              handleBlur,
              handleChange,
              setFieldValue,
              setTouched,
            }) => (
              <Form>
                <div className="w-full bg-bgWhite shadow drop-shadow-md rounded-lg py-5 px-6 mt-5">
                  <div>
                    <p className="text-xl mb-4">
                      {isEditMode === "duplicate"
                        ? "Duplicate Service Request"
                        : isEditMode === "edit"
                        ? "Edit Service Request"
                        : "New Service Request"}
                    </p>
                    <h2 className="text-base pb-6 font-normal text-textSecondary">
                      Basic Information
                    </h2>
                  </div>
                  <div className="grid grid-cols-2 gap-6 mb-4">
                    {/* Company name */}
                    <SelectWithLabel
                      idFromName="companyName"
                      placeholder="Company Name*"
                      options={companyOptions}
                      value={values?.companyName?.value || ""}
                      setFieldValue={setFieldValue}
                      errors={errors.companyName}
                      touched={touched.companyName}
                      endAdornment={
                        values?.companyName ? (
                          <IconButton
                            sx={{ position: "relative", right: "10px" }}
                            onClick={() => {
                              setFieldValue("companyName", "");
                              setFieldValue("customerFullName", "");
                              setFieldValue("outlet", []);
                              setCustomerOptions([]);
                              setOutletOptions([]);
                            }}
                          >
                            <ClearIcon sx={{ fontSize: "16px" }} />
                          </IconButton>
                        ) : null
                      }
                      onChange={(event) => {
                        setReinitialize(false);
                        const selectedOption = companyOptions?.find(
                          (option) => option.value === event.target.value
                        );

                        if (!selectedOption) return;

                        setFieldValue("companyName", selectedOption);
                        setFieldValue("customerFullName", "");
                        setFieldValue("outlet", []);
                        setCustomerOptions([]);
                        setOutletOptions([]);
                        setTouched({
                          ...touched,
                          companyName: false,
                          customerFullName: false,
                        });
                        handleCompanyChange(selectedOption?.value);
                      }}
                    />

                    {/* Customer Name */}
                    <SelectWithLabel
                      idFromName="customerFullName"
                      placeholder="Customer Name*"
                      options={customerOptions}
                      value={values?.customerFullName?.value || ""}
                      setFieldValue={setFieldValue}
                      helperText={
                        !values?.companyName
                          ? "Select a company name first"
                          : ""
                      }
                      errors={errors.customerFullName}
                      touched={touched.customerFullName}
                      endAdornment={
                        values?.customerFullName ? (
                          <IconButton
                            sx={{ position: "relative", right: "10px" }}
                            onClick={() => {
                              setFieldValue("customerFullName", "");
                            }}
                          >
                            <ClearIcon sx={{ fontSize: "16px" }} />
                          </IconButton>
                        ) : null
                      }
                      onChange={(event) => {
                        setReinitialize(false);
                        const selectedOption = customerOptions?.find(
                          (option) => option.value === event.target.value
                        );
                        setFieldValue("customerFullName", selectedOption);
                      }}
                    />
                    {/* Outlet */}
                    <SelectWithLabel
                      idFromName="outlet"
                      placeholder="Outlets"
                      options={outletOptions}
                      value={
                        values?.outlet?.map((option) => option.value) || []
                      }
                      helperText={
                        !values?.companyName
                          ? "Select a company name first"
                          : ""
                      }
                      setFieldValue={setFieldValue}
                      multiple
                      endAdornment={
                        Array.isArray(values?.outlet) &&
                        values?.outlet.length > 0 ? (
                          <IconButton
                            sx={{
                              position: "relative",
                              right: "10px",
                            }}
                            onClick={() => setFieldValue("outlet", [])}
                          >
                            <ClearIcon sx={{ fontSize: "16px" }} />
                          </IconButton>
                        ) : null
                      }
                      onChange={(event) => {
                        setReinitialize(false);
                        const selectedValues = event.target.value;
                        const selectedOptions = outletOptions.filter((option) =>
                          selectedValues.includes(option.value)
                        );
                        setSelectedOutlet(selectedOptions);
                        setFieldValue("outlet", selectedOptions);
                      }}
                      renderValue={(selected) => (
                        <>
                          {selected.map((value) => {
                            const selectedOption = outletOptions?.find(
                              (option) => option.value === value
                            );
                            return (
                              <Chip
                                key={value}
                                label={selectedOption?.label}
                                sx={{ marginRight: "5px" }}
                                clickable
                                variant="outlined"
                                deleteIcon={
                                  <Cancel
                                    onMouseDown={(event) =>
                                      event.stopPropagation()
                                    }
                                  />
                                }
                                onDelete={(e) =>
                                  handleDeleteOutlet(
                                    e,
                                    value,
                                    values,
                                    setFieldValue
                                  )
                                }
                              />
                            );
                          })}
                        </>
                      )}
                    />
                    {/* Technician */}
                    <SelectWithLabel
                      idFromName="technician"
                      placeholder="Technician"
                      options={techniciansOptions}
                      value={
                        values?.technician?.map((option) => option.value) || []
                      }
                      multiple
                      setFieldValue={setFieldValue}
                      endAdornment={
                        Array.isArray(values?.technician) &&
                        values?.technician.length > 0 ? (
                          <IconButton
                            sx={{
                              position: "relative",
                              right: "10px",
                            }}
                            onClick={() => setFieldValue("technician", [])}
                          >
                            <ClearIcon sx={{ fontSize: "16px" }} />
                          </IconButton>
                        ) : null
                      }
                      onChange={(event) => {
                        setReinitialize(false);
                        const selectedValues = event.target.value;
                        const selectedOptions = techniciansOptions.filter(
                          (option) => selectedValues.includes(option.value)
                        );
                        setFieldValue("technician", selectedOptions);
                      }}
                      renderValue={(selected) => (
                        <>
                          {selected.map((value) => {
                            const selectedOption = techniciansOptions?.find(
                              (option) => option.value === value
                            );
                            return (
                              <Chip
                                sx={{ marginRight: "5px" }}
                                key={value}
                                label={selectedOption?.label}
                                variant="outlined"
                                onDelete={(e) =>
                                  handleDeleteTechnician(
                                    e,
                                    value,
                                    values,
                                    setFieldValue
                                  )
                                }
                                deleteIcon={
                                  <Cancel
                                    onMouseDown={(event) =>
                                      event.stopPropagation()
                                    }
                                  />
                                }
                              />
                            );
                          })}
                        </>
                      )}
                    />
                    {/* Address */}
                    <InputWithLabel
                      className={`text-black border ${
                        errors.address && touched.address
                          ? "border-red"
                          : "border-borderTheme/80 focus-within:border-themeBtn"
                      } transition duration-300 ease-in-out`}
                      idFromName="address"
                      label="Address*"
                      type="address"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.address}
                      errors={errors.address && touched.address}
                      errorsText={errors.address}
                    />
                    {/* Postal Code */}
                    <InputWithLabel
                      className={`text-black border ${
                        errors.postalCode && touched.postalCode
                          ? "border-red"
                          : "border-borderTheme/80 focus-within:border-themeBtn"
                      } transition duration-300 ease-in-out`}
                      idFromName="postalCode"
                      label="Postal Code*"
                      type={"number"}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.postalCode}
                      errors={errors.postalCode && touched.postalCode}
                      errorsText={errors.postalCode}
                      onFocus={(e) =>
                        e.target.addEventListener(
                          "wheel",
                          function (e) {
                            e.preventDefault();
                          },
                          { passive: false }
                        )
                      }
                    />
                    {/* Phone */}
                    <div>
                      <InputWithLabel
                        className={
                          "text-black  border hover:border-themeBtn border-borderTheme/80 focus-within:border-themeBtn  transition duration-300 ease-in-out"
                        }
                        idFromName="contactNumber"
                        label="Mobile*"
                        type={"number"}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.contactNumber}
                        onFocus={(e) =>
                          e.target.addEventListener(
                            "wheel",
                            function (e) {
                              e.preventDefault();
                            },
                            { passive: false }
                          )
                        }
                      />
                    </div>
                    {/* Email */}
                    <InputWithLabel
                      className={
                        "text-black  border hover:border-themeBtn border-borderTheme/80 focus-within:border-themeBtn  transition duration-300 ease-in-out"
                      }
                      idFromName="email"
                      label="Email*"
                      type="email"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.email}
                    />
                    {/* Date */}
                    <div>
                      <DatePicker
                        value={
                          values?.date
                            ? moment(values?.date, "DD/MM/YYYY")
                            : null
                        }
                        onChange={(date) => {
                          setReinitialize(false);
                          setFieldValue("date", date ? date.toDate() : "");
                        }}
                        label="Select Date*"
                        format="DD/MM/YYYY"
                        error={errors.date}
                        touched={touched.date}
                        icon={null}
                      />
                    </div>
                    <SelectWithLabel
                      idFromName="timeRange"
                      placeholder="Time Range*"
                      options={handleTimeRangeChange()}
                      value={values?.timeRange?.value || ""}
                      setFieldValue={setFieldValue}
                      errors={errors.timeRange}
                      touched={touched.timeRange}
                      endAdornment={
                        values?.timeRange ? (
                          <IconButton
                            sx={{
                              position: "relative",
                              right: "10px",
                            }}
                            onClick={() => setFieldValue(`timeRange`, "")}
                          >
                            <ClearIcon sx={{ fontSize: "16px" }} />
                          </IconButton>
                        ) : null
                      }
                      onChange={(event) => {
                        setReinitialize(false);
                        const selectedOption = handleTimeRangeChange().find(
                          (option) => option.value === event.target.value
                        );
                        setFieldValue("timeRange", selectedOption);
                      }}
                    />
                  </div>
                </div>

                {/* Second Form */}
                {requestTypes?.map((requestType, requestIndex) => (
                  <div
                    key={requestIndex}
                    className="w-full bg-bgWhite shadow drop-shadow-md rounded-lg py-5 px-6 mt-4"
                  >
                    <div className="flex justify-between">
                      <p className="text-base text-textSecondary">
                        Request Types:
                      </p>
                      {requestIndex === 0 ? (
                        <AddPrimaryButton
                          value="Add Request Types"
                          onClick={handleAddRequestType}
                        />
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleRemoveRequest(requestIndex)}
                        >
                          <DeleteIcon />
                        </button>
                      )}
                    </div>
                    <div className="flex flex-col gap-2">
                      <RadioButton
                        idFrom={`repair-${requestIndex}`}
                        name={`group-${requestIndex}`}
                        label="Repair"
                        value={1}
                        checked={
                          values?.requestTypes?.[requestIndex]
                            ?.request_type_id === 1
                        }
                        onChange={(e) => {
                          setReinitialize(false);
                          const value = Number(e.target.value);
                          UpdateValue(
                            requestIndex,
                            null,
                            "request_type_id",
                            value
                          );
                          setFieldValue(
                            `requestTypes[${requestIndex}].request_type_id`,
                            value
                          );
                        }}
                      />
                      <RadioButton
                        idFrom={`followRepair-${requestIndex}`}
                        name={`group-${requestIndex}`}
                        label="Follow Up Repair"
                        value={2}
                        checked={
                          values?.requestTypes?.[requestIndex]
                            ?.request_type_id === 2
                        }
                        onChange={(e) => {
                          setReinitialize(false);
                          const value = Number(e.target.value);
                          UpdateValue(
                            requestIndex,
                            null,
                            "request_type_id",
                            value
                          );
                          setFieldValue(
                            `requestTypes[${requestIndex}].request_type_id`,
                            value
                          );
                        }}
                      />
                      <RadioButton
                        idFrom={`servicing-${requestIndex}`}
                        name={`group-${requestIndex}`}
                        label="Servicing"
                        value={3}
                        checked={
                          values?.requestTypes?.[requestIndex]
                            ?.request_type_id === 3
                        }
                        onChange={(e) => {
                          setReinitialize(false);
                          const value = Number(e.target.value);
                          UpdateValue(
                            requestIndex,
                            null,
                            "request_type_id",
                            value
                          );
                          setFieldValue(
                            `requestTypes[${requestIndex}].request_type_id`,
                            value
                          );
                        }}
                      />
                    </div>
                    <div
                      className={`${
                        isEditMode === "edit" ||
                        selectedRoles.length > 0 ||
                        values?.requestTypes?.[requestIndex]?.request_type_id
                          ? "border border-borderLine rounded-lg px-4 py-4 mt-3"
                          : ""
                      }`}
                    >
                      {values?.requestTypes?.[requestIndex]
                        ?.request_type_id && (
                        <div>
                          <div>
                            {requestType?.services?.map(
                              (service, serviceIndex) => (
                                <div
                                  className={`relative bg-white rounded-lg  ${
                                    serviceIndex !== 0 ? "mr-10" : "w-full"
                                  } `}
                                  key={serviceIndex}
                                >
                                  {serviceIndex !== 0 && (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleRemoveService(
                                          requestIndex,
                                          serviceIndex
                                        )
                                      }
                                      className="absolute top-0 -right-8 py-2 px-1 text-red-500"
                                    >
                                      <Close />
                                    </button>
                                  )}
                                  {serviceIndex === 0 ? (
                                    <div>
                                      <p className="text-xs pb-2 font-normal text-primaryBlack">
                                        Type of equipment
                                      </p>
                                      <div className="flex gap-4">
                                        {mockRoles?.map((role) => (
                                          <div
                                            key={role.id}
                                            className="mb-4 ml-2"
                                          >
                                            <label className="flex items-center cursor-pointer">
                                              <input
                                                type="checkbox"
                                                id={role.id}
                                                name="role"
                                                value={role.value}
                                                className="w-4 h-4 mr-1 text-center accent-themeBtn"
                                                checked={
                                                  values?.requestTypes?.[
                                                    requestIndex
                                                  ]?.services?.[
                                                    serviceIndex
                                                  ]?.type?.includes(
                                                    role.value
                                                  ) || false
                                                }
                                                onChange={(e) =>
                                                  handleRolesChange(
                                                    e,
                                                    requestIndex,
                                                    setFieldValue,
                                                    values,
                                                    serviceIndex
                                                  )
                                                }
                                              />
                                              <p className="text-primaryBlack font-normal text-sm font-Proxima pl-2">
                                                {role.label}
                                              </p>
                                            </label>
                                          </div>
                                        ))}
                                      </div>

                                      <InputWithLabel
                                        className={`text-black border ${
                                          errors?.requestTypes?.[requestIndex]
                                            ?.services?.[serviceIndex]?.title &&
                                          touched?.requestTypes?.[requestIndex]
                                            ?.services?.[serviceIndex]?.title
                                            ? "border-red"
                                            : "border-borderTheme/80 focus-within:border-themeBtn"
                                        } transition duration-300 ease-in-out`}
                                        idFromName={`requestTypes.${requestIndex}.services.${serviceIndex}.title`}
                                        label="Title*"
                                        type="text"
                                        onChange={(e) => {
                                          setReinitialize(false);
                                          const value = e.target.value;
                                          UpdateValue(
                                            requestIndex,
                                            serviceIndex,
                                            "title",
                                            value
                                          );
                                          setFieldValue(
                                            `requestTypes[${requestIndex}].services[${serviceIndex}].title`,
                                            value
                                          );
                                        }}
                                        value={service?.title || ""}
                                        onBlur={handleBlur}
                                        errors={
                                          errors?.requestTypes?.[requestIndex]
                                            ?.services?.[serviceIndex]?.title &&
                                          touched?.requestTypes?.[requestIndex]
                                            ?.services?.[serviceIndex]?.title
                                        }
                                        errorsText={
                                          errors?.requestTypes?.[requestIndex]
                                            ?.services?.[serviceIndex]?.title
                                        }
                                      />
                                    </div>
                                  ) : null}

                                  {serviceIndex === 0 && (
                                    <div className="mt-4">
                                      <InputWithLabel
                                        className={`text-black border ${
                                          errors?.requestTypes?.[requestIndex]
                                            ?.services?.[serviceIndex]
                                            ?.description &&
                                          touched?.requestTypes?.[requestIndex]
                                            ?.services?.[serviceIndex]
                                            ?.description
                                            ? "border-red"
                                            : "border-borderTheme/80 focus-within:border-themeBtn"
                                        } transition duration-300 ease-in-out`}
                                        idFromName={`remark-${requestIndex}-${serviceIndex}`}
                                        label="Description*"
                                        type="text"
                                        onBlur={handleBlur}
                                        onChange={(e) => {
                                          setReinitialize(false);
                                          const value = e.target.value;
                                          UpdateValue(
                                            requestIndex,
                                            serviceIndex,
                                            "description",
                                            value
                                          );
                                          setFieldValue(
                                            `requestTypes[${requestIndex}].services[${serviceIndex}].description`,
                                            value
                                          );
                                        }}
                                        value={service?.description || ""}
                                        errors={
                                          errors?.requestTypes?.[requestIndex]
                                            ?.services?.[serviceIndex]
                                            ?.description &&
                                          touched?.requestTypes?.[requestIndex]
                                            ?.services?.[serviceIndex]
                                            ?.description
                                        }
                                        errorsText={
                                          errors?.requestTypes?.[requestIndex]
                                            ?.services?.[serviceIndex]
                                            ?.description
                                        }
                                      />
                                    </div>
                                  )}

                                  <div className="grid grid-cols-3 gap-6 my-4">
                                    <SelectWithLabel
                                      idFromName={`requestTypes.${requestIndex}.services.${serviceIndex}.item`}
                                      placeholder="Item *"
                                      options={itemOptions}
                                      value={
                                        values?.requestTypes?.[requestIndex]
                                          ?.services?.[serviceIndex]?.item
                                          ?.value || ""
                                      }
                                      setFieldValue={setFieldValue}
                                      errors={
                                        errors?.requestTypes?.[requestIndex]
                                          ?.services?.[serviceIndex]?.item
                                      }
                                      touched={
                                        touched?.requestTypes?.[requestIndex]
                                          ?.services?.[serviceIndex]?.item
                                      }
                                      endAdornment={
                                        values?.requestTypes?.[requestIndex]
                                          ?.services?.[serviceIndex]?.item
                                          ?.value ? (
                                          <IconButton
                                            sx={{
                                              position: "relative",
                                              right: "10px",
                                            }}
                                            onClick={() =>
                                              setFieldValue(
                                                `requestTypes[${requestIndex}].services[${serviceIndex}].item`,
                                                ""
                                              )
                                            }
                                          >
                                            <ClearIcon
                                              sx={{ fontSize: "16px" }}
                                            />
                                          </IconButton>
                                        ) : null
                                      }
                                      onChange={(event) => {
                                        setReinitialize(false);
                                        const selectedOption = itemOptions.find(
                                          (option) =>
                                            option.value === event.target.value
                                        );
                                        setFieldValue(
                                          `requestTypes[${requestIndex}].services[${serviceIndex}].item`,
                                          selectedOption
                                        );
                                      }}
                                    />
                                    <div>
                                      <InputWithLabel
                                        className={`text-black border ${
                                          errors[
                                            `quantity-${requestIndex}-${serviceIndex}`
                                          ] &&
                                          touched[
                                            `quantity-${requestIndex}-${serviceIndex}`
                                          ]
                                            ? "border-red"
                                            : "border-borderTheme/80 focus-within:border-themeBtn"
                                        } transition duration-300 ease-in-out`}
                                        idFromName={`quantity-${requestIndex}-${serviceIndex}`}
                                        label="Quantity*"
                                        type="number"
                                        onBlur={handleBlur}
                                        onChange={(e) => {
                                          setReinitialize(false);
                                          const value = e.target.value;
                                          UpdateValue(
                                            requestIndex,
                                            serviceIndex,
                                            "quantity",
                                            value
                                          );
                                          setFieldValue(
                                            `requestTypes[${requestIndex}].services[${serviceIndex}].quantity`,
                                            value
                                          );
                                        }}
                                        onFocus={(e) =>
                                          e.target.addEventListener(
                                            "wheel",
                                            function (e) {
                                              e.preventDefault();
                                            },
                                            { passive: false }
                                          )
                                        }
                                        value={service?.quantity || ""}
                                        errors={
                                          errors?.requestTypes?.[requestIndex]
                                            ?.services?.[serviceIndex]
                                            ?.quantity &&
                                          touched?.requestTypes?.[requestIndex]
                                            ?.services?.[serviceIndex]?.quantity
                                        }
                                        errorsText={
                                          errors?.requestTypes?.[requestIndex]
                                            ?.services?.[serviceIndex]?.quantity
                                        }
                                      />
                                    </div>
                                    <div>
                                      <div>
                                        <InputWithLabel
                                          className={`text-black border ${
                                            errors[
                                              `pricing-${requestIndex}-${serviceIndex}`
                                            ] &&
                                            touched[
                                              `pricing-${requestIndex}-${serviceIndex}`
                                            ]
                                              ? "border-red"
                                              : "border-borderTheme/80 focus-within:border-themeBtn"
                                          } transition duration-300 ease-in-out`}
                                          idFromName={`pricing-${requestIndex}-${serviceIndex}`}
                                          label="Pricing ($)*"
                                          type="number"
                                          onBlur={handleBlur}
                                          onChange={(e) => {
                                            setReinitialize(false);
                                            const value = e.target.value;
                                            UpdateValue(
                                              requestIndex,
                                              serviceIndex,
                                              "pricing",
                                              value
                                            );
                                            setFieldValue(
                                              `requestTypes[${requestIndex}].services[${serviceIndex}].pricing`,
                                              value
                                            );
                                          }}
                                          onFocus={(e) =>
                                            e.target.addEventListener(
                                              "wheel",
                                              function (e) {
                                                e.preventDefault();
                                              },
                                              { passive: false }
                                            )
                                          }
                                          value={service?.pricing || ""}
                                          errors={
                                            errors?.requestTypes?.[requestIndex]
                                              ?.services?.[serviceIndex]
                                              ?.pricing &&
                                            touched?.requestTypes?.[
                                              requestIndex
                                            ]?.services?.[serviceIndex]?.pricing
                                          }
                                          errorsText={
                                            errors?.requestTypes?.[requestIndex]
                                              ?.services?.[serviceIndex]
                                              ?.pricing
                                          }
                                        />
                                      </div>
                                      {errors.errors?.requestTypes?.[
                                        requestIndex
                                      ]?.services?.[serviceIndex]?.pricing &&
                                        touched?.requestTypes?.[requestIndex]
                                          ?.services?.[serviceIndex]?.pricing &&
                                        touched.errors?.requestTypes?.[
                                          requestIndex
                                        ]?.services?.[serviceIndex]?.pricing &&
                                        touched?.requestTypes?.[requestIndex]
                                          ?.services?.[serviceIndex]
                                          ?.pricing && (
                                          <InputError
                                            errorTitle={
                                              errors.errors?.requestTypes?.[
                                                requestIndex
                                              ]?.services?.[serviceIndex]
                                                ?.pricing
                                            }
                                          />
                                        )}
                                    </div>
                                  </div>
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      )}
                      {requestTypes[requestIndex] && (
                        <div className="flex items-center justify-center my-4">
                          <div className="flex-grow border-t border-bgBtnGray"></div>
                          <button
                            type="button"
                            onClick={() => handleAddService(requestIndex)}
                            className={`bg-bgBtnGray w-32 h-10 rounded-4xl flex justify-center items-center shadow-lg mx-4 px-4 py-2 `}
                          >
                            <Plus className="fill-black mr-2" />
                            <p className="font-Proxima text-sm">ADD ITEMS</p>
                          </button>
                          <div className="flex-grow border-t border-bgBtnGray"></div>
                        </div>
                      )}
                      {/* upload image & video */}
                      {loadings ? (
                        <RippleLoader />
                      ) : (
                        <>
                          {isEditMode === "edit" &&
                          contractData?.is_mobile === 1 ? (
                            <div>
                              <p className="text-base mb-2 font-Proxima text-textSecondary">
                                Images:
                              </p>
                              <div>
                                <FileUploadChoose
                                  className="border border-borderTheme/80 transition duration-300 ease-in-out"
                                  handleFileChange={(event) =>
                                    handleFileChange(event, setFieldValue)
                                  }
                                  uploadedFiles={uploadedFiles}
                                  onDelete={(index) =>
                                    handleDelete(index, setFieldValue)
                                  }
                                  accept="image/*,video/*"
                                  deleteIcon={<CancelIcon />}
                                  uploadIcon={<CameraIcon />}
                                  uploadText={
                                    <>
                                      <p className="text-base mb-2 text-textSecondary">
                                        Add Media (Image or Video)
                                      </p>
                                    </>
                                  }
                                />
                              </div>
                            </div>
                          ) : null}
                        </>
                      )}
                    </div>
                  </div>
                ))}
                {/* summary */}
                {requestTypes?.some((requestType) =>
                  requestType?.services?.some(
                    (service) => service?.quantity && service?.pricing
                  )
                ) && (
                  <div className="w-full bg-bgWhite shadow drop-shadow-md rounded-lg py-5 px-6 mt-4">
                    <div>
                      <h2 className="text-xs pb-4 font-normal text-primaryBlack">
                        Summary:
                      </h2>
                    </div>
                    {requestTypes?.map((requestType, requestIndex) => (
                      <div key={requestIndex}>
                        {requestType?.services?.map((service, serviceIndex) => (
                          <div key={serviceIndex} className="flex">
                            <p className="text-sm text-textSecondary">
                              Total Price:
                            </p>
                            <p className="text-sm text-themeBtn ml-1">
                              $
                              {(
                                service?.quantity * service?.pricing
                              ).toLocaleString()}
                            </p>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
                {/* Remark */}
                <div className="w-full bg-bgWhite shadow drop-shadow-md rounded-lg py-5 px-6 mt-4">
                  <div>
                    <h2 className="text-xs pb-4 font-normal text-primaryBlack">
                      Remarks (Optional)
                    </h2>
                  </div>
                  <div>
                    <InputWithLabel
                      className={`text-black border ${
                        errors.remark && touched.remark
                          ? "border-red"
                          : "border-borderTheme/80 focus-within:border-themeBtn"
                      } transition duration-300 ease-in-out`}
                      idFromName="remark"
                      label="Description"
                      type="textarea"
                      rows={4}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.remark}
                      onInput={(e) => {
                        e.target.style.height = "auto";
                        e.target.style.height = `${e.target.scrollHeight}px`;
                      }}
                    />
                  </div>
                </div>
                {/* button */}
                <div className="flex justify-end gap-3 mt-3">
                  <DefaultPrimaryButton
                    type="button"
                    value="Cancel"
                    onClick={() => navigate("/services")}
                  />
                  <>
                    <DefaultButton type="submit" value="Save" />
                  </>
                </div>
              </Form>
            )}
          </Formik>
        )}
      </div>
      {showConfirmationModal && (
        <div>
          <ConfirmationModel
            confirmText={isEditMode === "edit" ? "Edit" : "Create"}
            onClose={() => setShowConfirmationModal(false)}
            onConfirm={() => handleConfirm(formValues)}
            isLoading={isLoading}
          />
        </div>
      )}
    </>
  );
};

export default CreateEditRequest;
